<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import OfferCard from './OfferCard.vue'
import OfferDetail from './OfferDetail.vue'
import StorefrontLoader from './StorefrontLoader.vue'

// Elevation presets on Tailwind's box-shadow scale, shared by the global theme and per-element
// (component) overrides so both resolve a shadow keyword to the same CSS value.
const SHADOWS = {
  none: 'none',
  xs: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  sm: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
}
function resolveShadow(keyword, fallback = 'sm') {
  return SHADOWS[keyword] || SHADOWS[fallback]
}

const props = defineProps({
  // SiteConfigContent-shaped: { branding, site, featureFlags, offerConfig }
  config: { type: Object, default: () => ({}) },
  offers: { type: Array, default: () => [] },
  // Whole-page loading (config not ready yet) - shows only the loader.
  loading: { type: Boolean, default: false },
  // Offers still loading - shows a spinner inside the offers section.
  offersLoading: { type: Boolean, default: false },
  // Persist dark-mode to localStorage (true in the live app, false in previews).
  persist: { type: Boolean, default: false },
  // Design-inspection mode (theme editor): clicking a region (header, hero, chips, card,
  // footer) emits `inspect` with the region key instead of its normal behaviour.
  inspect: { type: Boolean, default: false },
  // Which page the preview shows: 'home' (offer wall) or 'offer' (first offer's brand page).
  // Lets the theme editor reach the brand page while inspect mode swallows card clicks.
  previewPage: { type: String, default: null },
  // Editor override for dark mode: true/false pins the mode (the theme editor's preview
  // switch); null leaves it to the visitor's toggle.
  forceDark: { type: Boolean, default: null },
})
const emit = defineEmits(['inspect'])

// Capture-phase handler on each region: in inspect mode it swallows the click
// (so search boxes, toggles and cards don't react) and reports the region.
function onRegionClick(e, region) {
  if (!props.inspect) return
  e.stopPropagation()
  e.preventDefault()
  emit('inspect', region)
}

const branding = computed(() => props.config.branding || {})
const site = computed(() => props.config.site || {})
const flags = computed(() => props.config.featureFlags || {})
// Category chips: the publisher's curated list when set, otherwise every category present
// on the live offers (offer + retailer categories merged by the backend). Only categories
// that actually have offers are shown so a chip never leads to an empty wall.
const activeCategory = ref(null)
const categories = computed(() => {
  const enabled = props.config.offerConfig?.enabledCategories || []
  const present = new Set()
  for (const r of props.offers || []) for (const c of r.categories || []) present.add(c)
  if (enabled.length) return enabled.filter((c) => present.size === 0 || present.has(c))
  return [...present].sort()
})
function selectCategory(cat) {
  if (props.inspect) return
  activeCategory.value = activeCategory.value === cat ? null : cat
  if (selected.value) closeOffer()
}
watch(categories, (list) => {
  if (activeCategory.value && !list.includes(activeCategory.value)) activeCategory.value = null
})

// ── Internal UI state ────────────────────────────────────────────────
const search = ref('')
const dark = ref(props.persist ? localStorage.getItem('wl-dark') === 'true' : false)

function toggleDark() {
  dark.value = !dark.value
  if (props.persist) localStorage.setItem('wl-dark', String(dark.value))
}

// ── Offer detail view ────────────────────────────────────────────────
// Clicking a card opens the retailer's detail overlay. In the live app (persist)
// the selection is mirrored to the URL hash (#/brand/<id>) so links are shareable
// and the browser back button closes the view.
const selected = ref(null)
const rootEl = ref(null)

// Jump to the top on page change (works for both the live window and the
// editor's scrolling preview frame).
function scrollTop() {
  // scrollIntoView is missing in jsdom (unit tests) and older embedded webviews.
  if (typeof rootEl.value?.scrollIntoView === 'function') {
    rootEl.value.scrollIntoView({ block: 'start' })
  }
}

function hashId() {
  const m = window.location.hash.match(/^#\/brand\/(.+)$/)
  return m ? decodeURIComponent(m[1]) : null
}
function openOffer(offer) {
  selected.value = offer
  if (props.persist && hashId() !== offer.id) {
    window.location.hash = `#/brand/${encodeURIComponent(offer.id)}`
  }
  scrollTop()
}
watch(
  () => [props.previewPage, props.offers],
  ([page, offers]) => {
    if (page === 'offer') selected.value = offers?.[0] || null
    else if (page === 'home') selected.value = null
  },
  { immediate: true }
)

function closeOffer() {
  selected.value = null
  if (props.persist && hashId()) {
    history.pushState('', document.title, window.location.pathname + window.location.search)
  }
  scrollTop()
}
function syncFromHash() {
  const id = hashId()
  selected.value = id ? props.offers.find((r) => r.id === id) || null : null
}
onMounted(() => {
  if (!props.persist) return
  window.addEventListener('hashchange', syncFromHash)
  syncFromHash()
})
onUnmounted(() => {
  if (props.persist) window.removeEventListener('hashchange', syncFromHash)
})
// Offers arrive async - resolve a deep link once they land.
watch(
  () => props.offers,
  () => {
    if (props.persist && !selected.value) syncFromHash()
  }
)

// Typing in the header search navigates back to the results list.
watch(search, (term) => {
  if (term && selected.value) closeOffer()
})

// ── Search (header pill + typeahead) and sorting ─────────────────────
const searchFocused = ref(false)
// Small screens hide the inline search box; a header icon opens this full-screen modal instead.
const searchModalOpen = ref(false)
const searchModalInput = ref(null)
function openSearchModal() {
  if (props.inspect) return
  searchModalOpen.value = true
  requestAnimationFrame(() => searchModalInput.value?.focus())
}
function closeSearchModal() {
  searchModalOpen.value = false
  search.value = ''
}
function pickFromModal(offer) {
  closeSearchModal()
  openOffer(offer)
}
const sortBy = ref('popular')

function cashLabel(offer) {
  const v = offer.highestCashbackValue
  if (v == null) return ''
  const type = String(offer.highestCashbackType || '')
  return type.includes('PERCENTAGE') ? `${v}% cashback` : `£${v} cashback`
}

// The offer wall is filtered by category only. Typing in the search box never narrows the
// wall - matches are shown in the search dropdown, and picking one opens that offer.
const filteredOffers = computed(() => {
  const cat = activeCategory.value
  return props.offers.filter((r) => !cat || (r.categories || []).includes(cat))
})
const searchMatches = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return []
  return props.offers.filter((r) => (r.retailerName || '').toLowerCase().includes(term))
})

const visibleOffers = computed(() => {
  const sorted = [...filteredOffers.value]
  if (sortBy.value === 'cashback') {
    sorted.sort((a, b) => (b.highestCashbackValue || 0) - (a.highestCashbackValue || 0))
  } else if (sortBy.value === 'name') {
    sorted.sort((a, b) => (a.retailerName || '').localeCompare(b.retailerName || ''))
  } else {
    // Most popular: the brands with the most live offers first.
    sorted.sort((a, b) => (b.offerCount || 0) - (a.offerCount || 0))
  }
  return sorted
})

// Top matches shown in the search dropdown while typing.
const searchSuggestions = computed(() => searchMatches.value.slice(0, 5))

function pickSuggestion(offer) {
  searchFocused.value = false
  search.value = ''
  openOffer(offer)
}
function clearSearch() {
  search.value = ''
}

// Footer legal/support links are publisher-entered; only ever emit http(s) hrefs and a
// clean mailto, so a stored javascript:/data: value can never become a live link.
function safeHref(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim()) ? url.trim() : ''
}
function safeEmail(email) {
  return typeof email === 'string' && /^[^\s<>"'()]+@[^\s<>"'()]+\.[^\s<>"'()]+$/.test(email.trim())
    ? email.trim()
    : ''
}
function titleCase(v) {
  return String(v)
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// ── Theming: branding → CSS variables on the root (light/dark aware) ──
// Dark only applies when the site enables it (and never before config loads);
// the editor's forceDark prop overrides the visitor toggle.
const effectiveDark = computed(() =>
  props.forceDark === null ? dark.value && !!flags.value.darkMode : props.forceDark
)

const rootStyle = computed(() => {
  const b = branding.value
  const d = effectiveDark.value
  // Custom dark palette from the theme; unset values fall back to the built-in dark
  // colours (with surface/muted/border derived from the custom ground when one is set).
  const customDark = !!(b.darkBackgroundColour || b.darkTextColour)
  const dbg = b.darkBackgroundColour || '#111827'
  const dtext = b.darkTextColour || '#f3f4f6'
  const bg = d ? dbg : b.backgroundColour || '#ffffff'
  const surface = d
    ? customDark
      ? `color-mix(in srgb, ${dbg} 88%, #ffffff)`
      : '#1f2937'
    : b.backgroundColour || '#ffffff'
  const text = d ? dtext : b.textColour || '#1f2937'
  const muted = d
    ? customDark
      ? `color-mix(in srgb, ${dtext} 55%, ${dbg})`
      : '#9ca3af'
    : '#6b7280'
  // Borders: theme-set colour per palette, derived/default otherwise - or none at all.
  const border =
    b.showBorders === false
      ? 'transparent'
      : d
        ? b.darkBorderColour ||
          (customDark ? `color-mix(in srgb, ${dtext} 16%, ${dbg})` : '#374151')
        : b.borderColour || '#e5e7eb'
  const primary = (d && b.darkPrimaryColour) || b.primaryColour || '#6b7280'
  const secondary = (d && b.darkSecondaryColour) || b.secondaryColour || '#374151'
  const button = (d && b.darkButtonColour) || b.buttonColour || '#111827'
  const buttonText = (d && b.darkButtonTextColour) || b.buttonTextColour || '#ffffff'
  const font = b.fontFamily
    ? `'${b.fontFamily}', system-ui, -apple-system, sans-serif`
    : "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
  const headingFont = b.headingFontFamily ? `'${b.headingFontFamily}', system-ui, sans-serif` : font
  // Image overlay (hero banner + CTA tiles). Strength/darken are 0-100 in the theme and
  // become 0-1 multipliers; unset = the homepage banner gradient at full strength.
  const pct = (v, fallback) =>
    v == null || v === '' || isNaN(Number(v))
      ? fallback
      : Math.min(100, Math.max(0, Number(v))) / 100
  // Default wash is a neutral dark blue-grey - never the brand primary - so photos keep
  // their own colours; a theme can still pick any overlay colour.
  const overlayColour = (d && b.darkOverlayColour) || b.overlayColour || '#1e293b'
  // Elevation, on Tailwind's box-shadow scale; applied to cards, banners, tiles and video.
  const shadow = resolveShadow(b.shadow)
  // Offers per row: a fixed 1-6 column grid, or the default responsive auto-fit when unset.
  const perRow = Number(b.offersPerRow)
  const offerColumns =
    Number.isInteger(perRow) && perRow >= 1 && perRow <= 6
      ? `repeat(${perRow}, minmax(0, 1fr))`
      : 'repeat(auto-fill, minmax(250px, 1fr))'
  return {
    '--brand-primary': primary,
    '--brand-secondary': secondary,
    '--brand-bg': bg,
    '--brand-surface': surface,
    '--brand-text': text,
    '--brand-muted': muted,
    '--brand-border': border,
    '--brand-button': button,
    '--brand-button-text': buttonText,
    '--brand-font': font,
    '--brand-heading-font': headingFont,
    '--brand-radius': b.borderRadius || '14px',
    '--brand-overlay-colour': overlayColour,
    '--brand-overlay-strength': String(pct(b.overlayStrength, 1)),
    '--brand-overlay-darken': String(pct(b.overlayDarken, 1)),
    '--brand-overlay-direction': b.overlayDirection || 'to bottom',
    '--brand-shadow': shadow,
    '--brand-offer-columns': offerColumns,
  }
})

// Per-component overrides: re-declare the brand CSS variables on the region's element so
// everything inside inherits the component's own colours/font, falling back to the globals.
function regionStyle(region) {
  const cs = (branding.value.componentStyles || {})[region]
  if (!cs) return {}
  const s = {}
  if (cs.backgroundColour) {
    s['--brand-surface'] = cs.backgroundColour
    s['--brand-bg'] = cs.backgroundColour
  }
  if (cs.textColour) s['--brand-text'] = cs.textColour
  if (cs.accentColour) s['--brand-primary'] = cs.accentColour
  if (cs.secondaryColour) s['--brand-secondary'] = cs.secondaryColour
  if (cs.fontFamily) {
    const font = `'${cs.fontFamily}', system-ui, -apple-system, sans-serif`
    s['--brand-font'] = font
    s['--brand-heading-font'] = font
    s.fontFamily = font
  }
  // Per-element border + elevation overrides (fall back to the theme globals when unset).
  if (cs.showBorders === false) s['--brand-border'] = 'transparent'
  else if (cs.borderColour) s['--brand-border'] = cs.borderColour
  if (cs.shadow) s['--brand-shadow'] = resolveShadow(cs.shadow)
  return s
}

// CTA sections: the accent tints the image, and on the banner the "background" colour is the
// button (there's no other surface), so it maps to the button variables rather than --brand-bg.
function ctaStyle(region) {
  const cs = (branding.value.componentStyles || {})[region]
  if (!cs) return {}
  const s = regionStyle(region)
  if (region === 'ctaBanner' || region === 'videoCta') {
    delete s['--brand-bg']
    delete s['--brand-surface']
    delete s['--brand-text']
    if (cs.backgroundColour) s['--brand-button'] = cs.backgroundColour
    if (cs.textColour) s['--brand-button-text'] = cs.textColour
  }
  return s
}

const heroStyle = computed(() =>
  branding.value.heroImageUrl
    ? {
        // Vertical wash: near-clear at the top, deepening to the bottom so the copy sits on a
        // solid ground - darker at the bottom than the top, per the design.
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,.06) 0%, rgba(0,0,0,.28) 45%, rgba(0,0,0,.72) 100%), url('${branding.value.heroImageUrl}')`,
      }
    : {}
)
</script>

<template>
  <div ref="rootEl" class="wl-root" :class="{ 'wl-inspect': inspect }" :style="rootStyle">
    <StorefrontLoader v-if="loading" />

    <template v-else>
      <header
        class="wl-header"
        :style="regionStyle('header')"
        @click.capture="onRegionClick($event, 'header')"
      >
        <div class="wl-container">
          <a class="wl-brand" href="#" @click.prevent>
            <img
              v-if="branding.logoUrl"
              :src="branding.logoUrl"
              :alt="site.siteTitle || 'Rewards'"
            />
            <span v-else>{{ site.siteTitle || 'Rewards' }}</span>
          </a>

          <div class="wl-header-actions">
            <button
              v-if="flags.darkMode"
              class="wl-icon-btn"
              type="button"
              title="Toggle theme"
              @click="toggleDark"
            >
              <svg
                v-if="effectiveDark"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <circle cx="12" cy="12" r="4" />
                <path
                  d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
                />
              </svg>
              <svg
                v-else
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
              </svg>
            </button>

            <button
              v-if="flags.search"
              class="wl-icon-btn wl-search-open"
              type="button"
              title="Search brands"
              aria-label="Search brands"
              @click="openSearchModal"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
            </button>

            <div v-if="flags.search" class="wl-search">
              <svg
                class="wl-search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                v-model="search"
                type="search"
                placeholder="Search brands…"
                @focus="searchFocused = true"
                @blur="searchFocused = false"
                @keydown.escape="clearSearch"
              />
              <button
                v-if="search"
                class="wl-search-clear"
                type="button"
                title="Clear"
                @mousedown.prevent
                @click="clearSearch"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>

              <!-- Typeahead: top brand matches while typing -->
              <div v-if="searchFocused && searchSuggestions.length" class="wl-search-dropdown">
                <button
                  v-for="s in searchSuggestions"
                  :key="s.id"
                  class="wl-suggestion"
                  type="button"
                  @mousedown.prevent="pickSuggestion(s)"
                >
                  <span class="wl-suggestion-logo">
                    <img v-if="s.logo" :src="s.logo" :alt="s.retailerName" />
                    <span v-else>{{ (s.retailerName || '?').charAt(0).toUpperCase() }}</span>
                  </span>
                  <span class="wl-suggestion-text">
                    <strong>{{ s.retailerName }}</strong>
                    <small>{{ cashLabel(s) }}</small>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <!-- Mobile search modal: same matches as the desktop typeahead, full screen. -->
      <div
        v-if="searchModalOpen"
        class="wl-search-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Search brands"
        @keydown.escape="closeSearchModal"
      >
        <div class="wl-search-modal-bar">
          <div class="wl-search wl-search-modal-input">
            <svg
              class="wl-search-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              ref="searchModalInput"
              v-model="search"
              type="search"
              placeholder="Search brands…"
            />
          </div>
          <button class="wl-search-modal-close" type="button" @click="closeSearchModal">
            Cancel
          </button>
        </div>
        <div class="wl-search-modal-results">
          <button
            v-for="s in searchMatches"
            :key="s.id"
            class="wl-suggestion"
            type="button"
            @click="pickFromModal(s)"
          >
            <span class="wl-suggestion-logo">
              <img v-if="s.logo" :src="s.logo" :alt="s.retailerName" />
              <span v-else>{{ (s.retailerName || '?').charAt(0).toUpperCase() }}</span>
            </span>
            <span class="wl-suggestion-text">
              <strong>{{ s.retailerName }}</strong>
              <small>{{ cashLabel(s) }}</small>
            </span>
          </button>
          <p v-if="search.trim() && !searchMatches.length" class="wl-search-modal-empty">
            No brands match "{{ search.trim() }}".
          </p>
        </div>
      </div>

      <!-- Brand detail page: replaces the hero + offer wall, header and footer stay put. -->
      <OfferDetail
        v-if="selected"
        :offer="selected"
        :show-banner="!!flags.showOfferBanners"
        :inspect="inspect"
        :header-style="regionStyle('offerHeader')"
        :section-styles="{
          ctaBanner: ctaStyle('ctaBanner'),
          sectionTitle: regionStyle('sectionTitle'),
          ctaTiles: ctaStyle('ctaTiles'),
          videoCta: ctaStyle('videoCta'),
          offerTerms: regionStyle('offerTerms'),
        }"
        @close="closeOffer"
        @inspect="emit('inspect', $event)"
      />

      <section v-if="!selected" class="wl-hero-wrap" @click.capture="onRegionClick($event, 'hero')">
        <div class="wl-container">
          <div
            class="wl-hero"
            :class="{ 'no-image': !branding.heroImageUrl }"
            :style="[regionStyle('hero'), heroStyle]"
          >
            <div class="wl-hero-copy">
              <h1>
                {{ site.heroTitle || site.siteTitle || 'Earn cashback on the brands you love' }}
              </h1>
              <p>
                {{
                  site.heroSubtitle ||
                  site.metaDescription ||
                  'Shop your favourite retailers and get rewarded every time.'
                }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <nav
        v-if="!selected && flags.categories && categories.length"
        class="wl-categories"
        :style="regionStyle('categories')"
        @click.capture="onRegionClick($event, 'categories')"
      >
        <div class="wl-container">
          <button
            type="button"
            class="wl-chip"
            :class="{ 'is-active': !activeCategory }"
            :aria-pressed="!activeCategory"
            @click="selectCategory(null)"
          >
            All
          </button>
          <button
            v-for="cat in categories"
            :key="cat"
            type="button"
            class="wl-chip"
            :class="{ 'is-active': activeCategory === cat }"
            :aria-pressed="activeCategory === cat"
            @click="selectCategory(cat)"
          >
            {{ titleCase(cat) }}
          </button>
        </div>
      </nav>

      <section v-if="!selected" class="wl-section">
        <div class="wl-container">
          <div v-if="offersLoading" class="wl-state">
            <div class="wl-spinner-sm" role="status" aria-label="Loading offers" />
          </div>
          <template v-else>
            <div class="wl-section-head">
              <h2>Cashback offers</h2>
              <div class="wl-sort">
                <span>Sort by:</span>
                <div class="wl-sort-pill">
                  <select v-model="sortBy" aria-label="Sort offers">
                    <option value="popular">Most popular</option>
                    <option value="cashback">Highest cashback</option>
                    <option value="name">A–Z</option>
                  </select>
                </div>
              </div>
            </div>
            <div
              v-if="visibleOffers.length"
              class="wl-offer-grid"
              :style="regionStyle('card')"
              @click.capture="onRegionClick($event, 'card')"
            >
              <OfferCard
                v-for="r in visibleOffers"
                :key="r.id"
                :offer="r"
                :show-banner="!!flags.showOfferBanners"
                @select="openOffer"
              />
            </div>
            <div v-else class="wl-state">
              <h3>No offers found</h3>
              <p>Try a different search.</p>
            </div>
          </template>
        </div>
      </section>

      <footer
        class="wl-footer"
        :style="regionStyle('footer')"
        @click.capture="onRegionClick($event, 'footer')"
      >
        <div class="wl-container">
          <div>{{ site.footerText || '' }}</div>
          <div class="wl-footer-links">
            <a
              v-if="safeHref(site.privacyPolicyUrl)"
              :href="safeHref(site.privacyPolicyUrl)"
              target="_blank"
              rel="noopener noreferrer"
              >Privacy</a
            >
            <a
              v-if="safeHref(site.termsUrl)"
              :href="safeHref(site.termsUrl)"
              target="_blank"
              rel="noopener noreferrer"
              >Terms</a
            >
            <a
              v-if="safeHref(site.cashbackTermsUrl)"
              :href="safeHref(site.cashbackTermsUrl)"
              target="_blank"
              rel="noopener noreferrer"
              >Cashback terms</a
            >
            <a
              v-if="safeHref(site.supportUrl)"
              :href="safeHref(site.supportUrl)"
              target="_blank"
              rel="noopener noreferrer"
              >Support</a
            >
            <a
              v-if="safeEmail(site.supportEmail)"
              :href="`mailto:${safeEmail(site.supportEmail)}`"
              >{{ safeEmail(site.supportEmail) }}</a
            >
          </div>
        </div>
      </footer>
    </template>
  </div>
</template>

<style scoped>
.wl-root {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--brand-bg);
  color: var(--brand-text);
  font-family: var(--brand-font);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}
.wl-root * {
  box-sizing: border-box;
}
.wl-root a {
  color: inherit;
  text-decoration: none;
}
.wl-root img {
  max-width: 100%;
}

.wl-container {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Header */
.wl-header {
  position: sticky;
  top: 0;
  z-index: 20;
  background: var(--brand-surface);
  border-bottom: 1px solid var(--brand-border);
}
/* Brand left, actions (theme toggle + search) pinned right. */
.wl-header .wl-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 72px;
}
.wl-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--brand-heading-font);
  font-weight: 700;
  font-size: 1.15rem;
}
.wl-brand img {
  height: 30px;
  width: auto;
  object-fit: contain;
}
.wl-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.wl-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: calc(var(--brand-radius, 14px) * 2.5);
  border: 0;
  background: color-mix(in srgb, var(--brand-text) 5%, var(--brand-bg));
  color: var(--brand-text);
  cursor: pointer;
}
.wl-icon-btn svg {
  width: 18px;
  height: 18px;
}

/* Search pill with typeahead dropdown */
.wl-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(320px, 40vw);
  height: 44px;
  padding: 0 12px;
  /* Always a pill, even on 0-radius themes. */
  border-radius: 999px;
  background: color-mix(in srgb, var(--brand-text) 5%, var(--brand-bg));
  color: var(--brand-text);
}
.wl-search-icon {
  flex: 0 0 auto;
  width: 17px;
  height: 17px;
  color: var(--brand-muted);
}
/* Form controls don't inherit font by default - force the theme font on the input and the
   dropdown's buttons so search matches the rest of the site. */
.wl-search input,
.wl-search-dropdown button {
  font: inherit;
}
.wl-search input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: none;
  color: inherit;
  font-size: 0.9rem;
  outline: none;
}
.wl-search input::-webkit-search-cancel-button {
  display: none;
}
.wl-search-clear {
  flex: 0 0 auto;
  display: inline-flex;
  border: 0;
  background: none;
  padding: 4px;
  color: var(--brand-muted);
  cursor: pointer;
}
.wl-search-clear svg {
  width: 14px;
  height: 14px;
}
.wl-search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  width: max(100%, 300px);
  padding: 6px;
  border-radius: var(--brand-radius, 14px);
  background: var(--brand-surface);
  box-shadow: 0 18px 40px -18px rgba(0, 0, 0, 0.35);
  border: 1px solid var(--brand-border);
}
.wl-suggestion {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 9px 10px;
  border: 0;
  border-radius: calc(var(--brand-radius, 14px) * 0.7);
  background: none;
  color: var(--brand-text);
  text-align: left;
  cursor: pointer;
}
.wl-suggestion:hover {
  background: color-mix(in srgb, var(--brand-text) 6%, var(--brand-surface));
}
.wl-suggestion-logo {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: var(--brand-bg);
  overflow: hidden;
  font-weight: 800;
  color: var(--brand-primary);
}
.wl-suggestion-logo img {
  max-width: 28px;
  max-height: 28px;
  object-fit: contain;
}
.wl-suggestion-text {
  display: grid;
  line-height: 1.25;
}
.wl-suggestion-text strong {
  font-size: 0.9rem;
}
.wl-suggestion-text small {
  color: var(--brand-muted);
  font-size: 0.8rem;
}

/* Hero: a rounded image card inside the container, copy anchored bottom-left. */
.wl-hero-wrap {
  padding-top: 28px;
}
.wl-hero {
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: 380px;
  border: 1px solid var(--brand-border, transparent);
  border-radius: calc(var(--brand-radius, 14px) * 1.6);
  overflow: hidden;
  color: #fff;
  box-shadow: var(--brand-shadow, none);
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.55) 95%), var(--brand-primary);
  background-size: cover;
  background-position: center;
}
.wl-hero.no-image {
  background: linear-gradient(120deg, var(--brand-primary), var(--brand-secondary));
}
.wl-hero-copy {
  padding: 26px 30px;
}
.wl-hero h1 {
  margin: 0 0 8px;
  font-family: var(--brand-heading-font);
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  font-weight: 800;
  text-wrap: balance;
}
.wl-hero p {
  margin: 0;
  font-size: 1.05rem;
  opacity: 0.92;
  max-width: 52ch;
}

/* Category chips */
.wl-categories {
  border-bottom: 1px solid var(--brand-border);
  background: var(--brand-surface);
}
.wl-categories .wl-container {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-top: 12px;
  padding-bottom: 12px;
}
.wl-chip.is-active {
  background: var(--brand-primary);
  border-color: var(--brand-primary);
  color: var(--brand-button-text, #fff);
}
.wl-chip {
  flex: 0 0 auto;
  cursor: pointer;
  font: inherit;
  padding: 7px 14px;
  border-radius: calc(var(--brand-radius, 14px) * 2.5);
  border: 1px solid var(--brand-border);
  background: var(--brand-bg);
  color: var(--brand-text);
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
}

/* Offers */
.wl-section {
  padding: 32px 0 64px;
}
.wl-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 16px;
}
.wl-section-head h2 {
  margin: 0;
  font-family: var(--brand-heading-font);
  font-size: 1.4rem;
  font-weight: 700;
}
.wl-sort {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--brand-muted);
  font-size: 0.9rem;
}
.wl-sort-pill {
  position: relative;
  border-radius: 999px;
  background: color-mix(in srgb, var(--brand-text) 5%, var(--brand-bg));
}
/* Chevron: the native arrow is suppressed (appearance: none), so draw our own. */
.wl-sort-pill::after {
  content: '';
  position: absolute;
  right: 16px;
  top: 50%;
  width: 7px;
  height: 7px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: translateY(-70%) rotate(45deg);
  color: var(--brand-text);
  pointer-events: none;
}
.wl-sort-pill select {
  appearance: none;
  border: 0;
  background: none;
  padding: 10px 36px 10px 18px;
  color: var(--brand-text);
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
}
.wl-offer-grid {
  display: grid;
  grid-template-columns: var(--brand-offer-columns, repeat(auto-fill, minmax(250px, 1fr)));
  gap: 14px;
}

/* States */
.wl-state {
  text-align: center;
  padding: 56px 20px;
  color: var(--brand-muted);
}
.wl-state h3 {
  color: var(--brand-text);
  margin: 0 0 6px;
}
.wl-spinner-sm {
  width: 34px;
  height: 34px;
  margin: 0 auto;
  border-radius: 50%;
  border: 3px solid var(--brand-border);
  border-top-color: var(--brand-primary);
  animation: wl-offers-spin 0.8s linear infinite;
}
@keyframes wl-offers-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Footer */
.wl-footer {
  /* Pinned to the bottom when the page is shorter than its container. */
  margin-top: auto;
  border-top: 1px solid var(--brand-border);
  background: var(--brand-surface);
  color: var(--brand-muted);
  font-size: 0.9rem;
}
.wl-footer .wl-container {
  padding: 28px 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  align-items: center;
  justify-content: space-between;
}
.wl-footer a:hover {
  color: var(--brand-text);
}
.wl-footer-links {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}

/* Inspect mode: clickable regions highlight on hover. */
.wl-inspect .wl-header:hover,
.wl-inspect .wl-hero:hover,
.wl-inspect .wl-categories:hover,
.wl-inspect .wl-footer:hover,
.wl-inspect .wl-offer-grid .wl-offer-card:hover {
  outline: 2px dashed var(--brand-primary);
  outline-offset: -2px;
  cursor: pointer;
}
.wl-inspect .wl-offer-grid .wl-offer-card:hover {
  transform: none;
}

/* Mobile search: icon in the header (hidden on desktop), full-screen modal. */
.wl-search-open {
  display: none;
}
.wl-search-modal {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  background: var(--brand-bg);
  color: var(--brand-text);
}
.wl-search-modal-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--brand-border);
}
.wl-search-modal-input {
  display: flex;
  flex: 1;
}
.wl-search-modal-close {
  border: 0;
  background: none;
  color: var(--brand-text);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.wl-search-modal-results {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}
.wl-search-modal-results .wl-suggestion {
  width: 100%;
}
.wl-search-modal-empty {
  margin: 24px 12px;
  color: var(--brand-muted);
  font-size: 0.95rem;
}

@media (max-width: 768px) {
  /* Ignore a large fixed offers-per-row on small screens. */
  .wl-offer-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
  .wl-search {
    display: none;
  }
  .wl-search-modal .wl-search {
    display: flex; /* the modal reuses the search box styles */
  }
  .wl-search-open {
    display: inline-flex;
  }
  .wl-container {
    padding: 0 16px;
  }
  .wl-header .wl-container {
    height: 60px;
  }
  .wl-hero-wrap {
    padding-top: 16px;
  }
  .wl-hero {
    min-height: 240px;
  }
  .wl-hero h1 {
    font-size: 1.5rem;
  }
  .wl-hero p {
    font-size: 0.95rem;
  }
  .wl-section {
    padding: 24px 0 40px;
  }
  .wl-section-head h2 {
    font-size: 1.05rem;
  }
  .wl-hero-copy {
    padding: 18px 20px;
  }
  .wl-sort {
    font-size: 0.85rem;
  }
  .wl-sort-pill select {
    padding: 8px 32px 8px 14px;
    font-size: 0.8rem;
  }
}
</style>
